import { useMemo } from 'react'
import { useSelector } from 'react-redux'
// eslint-disable-next-line import/no-cycle
import { useAppDispatch } from 'state'
import BigNumber from 'bignumber.js'
import { BIG_ZERO } from 'config/constants/bigNumber'
import { getBalanceAmount } from 'utils/formatBalance'
import { DEFAULT_DEPOSIT_USD } from 'config/constants/info'
import { getFarmsConfig } from 'config/farms'
import { useWagmi } from 'libs/wagmi'
import { useSlowRefreshEffect } from 'hooks/useRefreshEffect'
import { deserializeToken } from 'state/user/hooks/helpers'
import { addressesEqual } from 'utils/addressHelpers'
import {
  DeserializedFarm,
  DeserializedFarmsState,
  DeserializedFarmUserData,
  HolderBonusPool,
  SerializedFarm,
} from 'state/types'
import { fetchFarmBonusesAsync, fetchFarmsPublicDataAsync, fetchFarmUserDataAsync } from '.'
import { farmSelector } from './selectors'

const deserializeFarmUserData = (farm: SerializedFarm): DeserializedFarmUserData => ({
  allowance: farm.userData ? new BigNumber(farm.userData.allowance) : BIG_ZERO,
  tokenBalance: farm.userData ? new BigNumber(farm.userData.tokenBalance) : BIG_ZERO,
  stakedBalance: farm.userData ? new BigNumber(farm.userData.stakedBalance) : BIG_ZERO,
  earnings: farm.userData ? new BigNumber(farm.userData.earnings) : BIG_ZERO,
})

export const deserializeFarm = (farm: SerializedFarm): DeserializedFarm => ({
  ...farm,
  token: deserializeToken(farm.token),
  quoteToken: deserializeToken(farm.quoteToken),
  earningToken: deserializeToken(farm.earningToken),
  userData: deserializeFarmUserData(farm),
  tokenAmountTotal: farm.tokenAmountTotal ? new BigNumber(farm.tokenAmountTotal) : BIG_ZERO,
  quoteTokenAmountTotal: farm.quoteTokenAmountTotal ? new BigNumber(farm.quoteTokenAmountTotal) : BIG_ZERO,
  lpTotalInQuoteToken: farm.lpTotalInQuoteToken ? new BigNumber(farm.lpTotalInQuoteToken) : BIG_ZERO,
  lpTotalSupply: farm.lpTotalSupply ? new BigNumber(farm.lpTotalSupply) : BIG_ZERO,
  tokenPriceVsQuote: farm.tokenPriceVsQuote ? new BigNumber(farm.tokenPriceVsQuote) : BIG_ZERO,
  tokensPerSec: farm.tokensPerSec ? new BigNumber(farm.tokensPerSec) : BIG_ZERO,
})

export const usePollFarmsPublicData = (includeFinished = false) => {
  const dispatch = useAppDispatch()
  const { chainId } = useWagmi()
  const farmsConfig = getFarmsConfig(chainId)
  const liveFarms = farmsConfig.filter((farm) => !farm.finished)

  useSlowRefreshEffect(() => {
    const farms = includeFinished ? farmsConfig : liveFarms

    dispatch(fetchFarmsPublicDataAsync({ chainId, farms }))
  }, [dispatch, includeFinished, chainId])
}

export const usePollFarmsWithUserData = (farms: SerializedFarm[]) => {
  const dispatch = useAppDispatch()
  const { account, chainId } = useWagmi()

  useSlowRefreshEffect(() => {
    dispatch(fetchFarmsPublicDataAsync({ chainId, farms }))

    if (account) {
      dispatch(fetchFarmUserDataAsync({ account, chainId, farms }))
      // dispatch(fetchFarmBonusesAsync({ chainId, farms })) // Disabled - API endpoint no longer available
    }
  }, [dispatch, account])
}

export const useHolderBonusApr = () => {
  const getCurrentRewardInNmx = (holderBonusPool: HolderBonusPool, tokenPriceBusd: string): number =>
    Number(
      new BigNumber(DEFAULT_DEPOSIT_USD)
        .div(tokenPriceBusd)
        .div(holderBonusPool.effectivePoolSizeInLpTokens)
        .times(holderBonusPool.dailyPoolSizeNmx),
    )

  const getTotalStakedInBusd = (stakedBalance: BigNumber, tokenPriceBusd: string): BigNumber =>
    stakedBalance.gt(0) ? getBalanceAmount(stakedBalance).times(tokenPriceBusd) : new BigNumber(DEFAULT_DEPOSIT_USD)

  return { getTotalStakedInBusd, getCurrentRewardInNmx }
}

export const useFarms = (pids?: number[]): DeserializedFarmsState => {
  const { chainId } = useWagmi()
  const farms = useSelector(useMemo(() => farmSelector(chainId), [chainId]))
  const deserializedFarmsData = useMemo(
    () => farms.data.filter((farm) => (pids ? pids.includes(farm.pid) : true)).map(deserializeFarm),
    [farms, pids],
  )
  const { userDataLoaded } = farms

  return {
    data: deserializedFarmsData,
    userDataLoaded,
  }
}

export const useFarmFromAddress = (address: string): DeserializedFarm => {
  const { chainId } = useWagmi()
  const farms = useSelector(useMemo(() => farmSelector(chainId), [chainId]))
  const farm = farms.data.find((f) => addressesEqual(f.stakingServiceAddress, address))

  return deserializeFarm(farm)
}

export const useFarmFromLpAddress = (lpAddress: string): DeserializedFarm => {
  const { chainId } = useWagmi()
  const farms = useSelector(useMemo(() => farmSelector(chainId), [chainId]))
  const farm = farms.data.find((f) => addressesEqual(f.lpAddress, lpAddress))

  return farm ? deserializeFarm(farm) : null
}

export const useFarmFromPid = (pid: number): DeserializedFarm => {
  const { chainId } = useWagmi()
  const farms = useSelector(useMemo(() => farmSelector(chainId), [chainId]))
  const farm = farms.data.find((f) => f.pid === pid)

  return farm ? deserializeFarm(farm) : null
}

export const useFarmFromLpSymbol = (lpSymbol: string): DeserializedFarm | null => {
  const { chainId } = useWagmi()
  const farms = useSelector(useMemo(() => farmSelector(chainId), [chainId]))
  const farm = farms.data.find((f) => f.lpSymbol === lpSymbol)

  return farm ? deserializeFarm(farm) : null
}

export const useFarmUser = (pid): DeserializedFarmUserData | null => {
  const farm = useFarmFromPid(pid)

  if (!farm) {
    return null
  }

  const { userData } = farm
  const { allowance, tokenBalance, stakedBalance, earnings } = userData

  return {
    allowance,
    tokenBalance,
    stakedBalance,
    earnings,
  }
}

export const useFarmTokenPrices = () => {
  const { chainId } = useWagmi()
  const farms = useSelector(useMemo(() => farmSelector(chainId), [chainId]))

  return useMemo(
    () =>
      farms.data.reduce((prices, farm) => {
        const quoteTokenAddress = farm.quoteToken.address.toLocaleLowerCase()
        const tokenAddress = farm.token.address.toLocaleLowerCase()
        /* eslint-disable no-param-reassign */
        if (!prices[quoteTokenAddress]) {
          prices[quoteTokenAddress] = new BigNumber(farm.quoteTokenPriceBusd).toNumber()
        }
        if (!prices[tokenAddress]) {
          prices[tokenAddress] = new BigNumber(farm.tokenPriceBusd).toNumber()
        }
        /* eslint-enable no-param-reassign */
        return prices
      }, {}),
    [farms],
  )
}

// Return the base token price for a farm, from a given pid
export const useUsdtPriceFromPid = (pid: number): BigNumber => {
  const farm = useFarmFromPid(pid)
  return farm && new BigNumber(farm.tokenPriceBusd)
}
